<template>
  <div class="md-page">

    <div class="md-wrap">

      <!-- ===================================================== -->
      <!-- HERO HEADER                                           -->
      <!-- ===================================================== -->
      <header class="md-hero">
        <div class="md-hero-grid">
          <div class="md-hero-main">
            <div class="md-hero-label">Warehouse &amp; returns</div>
            <div class="md-hero-title">
              Merchandise due
              <transition name="md-pop">
                <span v-if="filter.length > 0" class="md-count-pill">{{ filter.length }}</span>
              </transition>
            </div>
            <div class="md-hero-tags">
          
            </div>
         
          </div>
        
        </div>

        <div class="md-hero-actions">
          <b-button class="md-btn-ghost-dark" @click="fetch_init" :disabled="items_being_loaded">
            <b-icon icon="arrow-clockwise"></b-icon> Refresh
          </b-button>
          <b-button
            class="md-btn-primary"
            :disabled="!filter.length || bulkBusy"
            @click="markAllReceived"
          >
            <b-spinner small v-if="bulkBusy"></b-spinner>
            <b-icon v-else icon="check2-all"></b-icon>
            Receive all ({{ filter.length }})
          </b-button>
        </div>
      </header>

      <!-- ===================================================== -->
      <!-- SINGLE-ROW FILTER STRIP                               -->
      <!-- ===================================================== -->
      <section class="md-card md-filterstrip">

        <div class="md-filters-bar">

          <!-- Date range -->
          <div class="md-fb-item md-fb-dates">
            <date-picker
              v-model="date_range"
              format="MM-DD-YYYY"
              valueType="format"
              class="md-fb-datepicker"
              placeholder="Date range"
              range
            ></date-picker>
          </div>

          <!-- Invoice -->
          <div class="md-fb-item md-fb-search">
            <b-input-group class="md-fb-inputgroup">
              <b-input-group-prepend>
                <b-input-group-text><b-icon icon="receipt"></b-icon></b-input-group-text>
              </b-input-group-prepend>
              <b-form-input
                v-model="search_invoice"
                placeholder="Invoice #"
                class="md-fb-input"
                @keyup.enter="fetch_init"
                autocomplete="off"
              ></b-form-input>
            </b-input-group>
          </div>

          <!-- SKU -->
          <div class="md-fb-item md-fb-search">
            <b-input-group class="md-fb-inputgroup">
              <b-input-group-prepend>
                <b-input-group-text><b-icon icon="upc-scan"></b-icon></b-input-group-text>
              </b-input-group-prepend>
              <b-form-input
                v-model="search_sku"
                placeholder="Item SKU"
                class="md-fb-input"
                @keyup.enter="fetch_init"
                autocomplete="off"
              ></b-form-input>
            </b-input-group>
          </div>

          <!-- Salesperson -->
          <div class="md-fb-item md-fb-pill">
            <b-icon icon="person-badge" class="md-fb-pill-icon"></b-icon>
            <b-form-select
              :options="salespersonOptions"
              v-model="picked_user"
              class="md-fb-select"
            ></b-form-select>
          </div>

          <!-- Location -->
          <div class="md-fb-item md-fb-pill">
            <b-icon icon="geo-alt" class="md-fb-pill-icon"></b-icon>
            <b-form-select
              :options="locationOptions"
              v-model="picked_location"
              class="md-fb-select"
            ></b-form-select>
          </div>

          <b-button class="md-btn-primary md-fb-search-btn" @click="fetch_init">
            <b-icon icon="search"></b-icon> Search
          </b-button>

          <b-button class="md-btn-ghost md-fb-reset" @click="resetFilters" title="Reset filters">
            <b-icon icon="arrow-counterclockwise"></b-icon>
          </b-button>
        </div>

        <!-- Date shortcuts row -->
        <div class="md-quick-dates">
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'today' }"
            @click="set_dates('today')"
          >Today</b-button>
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'yesterday' }"
            @click="set_dates('yesterday')"
          >Yesterday</b-button>
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'this_week' }"
            @click="set_dates('this_week')"
          >This week</b-button>
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'last_week' }"
            @click="set_dates('last_week')"
          >Last week</b-button>
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'this_month' }"
            @click="set_dates('this_month')"
          >This month</b-button>
          <b-button
            size="sm"
            class="md-quick-btn"
            :class="{ 'is-active': picked_date_range === 'last_month' }"
            @click="set_dates('last_month')"
          >Last month</b-button>
        </div>
      </section>

      <!-- ===================================================== -->
      <!-- TABLE                                                 -->
      <!-- ===================================================== -->
      <section class="md-card md-table-card">
        <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
          <div v-if="filter.length > 0" class="md-table-scroll">
            <table class="md-table">
              <thead>
                <tr>
                  <th class="md-th md-th--date" @click="sortby('created_ts')">
                    <span>Date</span> <span class="md-sort-icon">↕</span>
                  </th>
                  <th class="md-th" style="min-width: 150px" @click="sortby('invoice_id')">
                    <span>Invoice #</span> <span class="md-sort-icon">↕</span>
                  </th>
                  <th class="md-th" style="min-width: 210px" @click="sortby('customer')">
                    <span>Customer</span> <span class="md-sort-icon">↕</span>
                  </th>
                  <th class="md-th" style="min-width: 240px" @click="sortby('product_name')">
                    <span>Item description</span> <span class="md-sort-icon">↕</span>
                  </th>
                  <th class="md-th" style="min-width: 140px" @click="sortby('product_sku')">
                    <span>SKU</span> <span class="md-sort-icon">↕</span>
                  </th>
                  <th class="md-th md-th--num" style="min-width: 110px">Return</th>
                  <th class="md-th md-th--num" style="min-width: 110px">Received</th>
                  <th class="md-th md-th--num" style="min-width: 110px">Pending</th>
                  <th class="md-th" style="min-width: 260px">Return to</th>
                  <th class="md-th md-th--num" style="min-width: 120px">Receive qty</th>
                  <th class="md-th md-th--actions" style="min-width: 150px">Action</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(item, idx) in filter" :key="item.receipt_id" class="md-row">
                  <td class="md-td md-td--date">
                    <span class="md-date-cell">{{ formatDateOnly(item.created_ts) }}</span>
                  </td>
                  <td class="md-td">
                    <a href="#" @click.prevent="goToSale(item.invoice_id)" class="md-invoice-link">
                      <b-icon icon="receipt"></b-icon>
                      {{ item.invoice_id }}
                    </a>
                  </td>
                  <td class="md-td">
                    <div class="md-cust-cell">
                      <div class="md-cust-name" :title="customerFullName(item)">{{ customerName(item) }}</div>
                    </div>
                  </td>
                  <td class="md-td">
                    <div class="md-item-desc" :title="item.product_name">{{ item.product_name || '—' }}</div>
                  </td>
                  <td class="md-td">
                    <span class="md-sku">{{ item.product_sku }}</span>
                  </td>
                  <td class="md-td md-td--num">
                    <span class="md-chip">{{ item.return_quantity }}</span>
                  </td>
                  <td class="md-td md-td--num">
                    <span class="md-chip is-teal">{{ item.received_quantity }}</span>
                  </td>
                  <td class="md-td md-td--num">
                    <span class="md-chip is-warn">{{ item.pending_quantity }}</span>
                  </td>

                  <td class="md-td">
                    <b-form-select
                      size="sm"
                      v-model="receiveLocsub[item.receipt_id]"
                      :options="getReturnLocationOptions(item)"
                      :disabled="busy[item.receipt_id] || bulkBusy"
                      class="md-locsub-select"
                    ></b-form-select>
                  </td>

                  <td class="md-td md-td--num">
                    <b-input
                      size="sm"
                      type="number"
                      min="1"
                      :max="item.pending_quantity"
                      v-model="receiveQty[item.receipt_id]"
                      :disabled="busy[item.receipt_id] || bulkBusy"
                      class="md-qty-input"
                    ></b-input>
                  </td>

                  <td class="md-td md-td--actions">
                    <b-button
                      size="sm"
                      class="md-btn-receive"
                      @click="markReceived(item)"
                      :disabled="busy[item.receipt_id] || bulkBusy || !receiveLocsub[item.receipt_id]"
                    >
                      <b-spinner small v-if="busy[item.receipt_id]"></b-spinner>
                      <b-icon v-else icon="check2"></b-icon>
                      Receive
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="!items_being_loaded" class="md-empty">
            <div class="md-empty-icon"><b-icon icon="check2-circle" font-scale="2.4"></b-icon></div>
            <h3>No merchandise due</h3>
            <p>All returned items have been received. Nothing is pending.</p>
          </div>
        </b-overlay>
      </section>

    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import global_vars from '@/utilities/global_vars';
import Requestor from '@/utilities/Requestor';

const MECCA_RETURN_LOCSUB = "47|124";

export default {
  name: "MerchandiseDue",
  components: { DatePicker },
  data() {
    return {
      packet: {},
      items: [],
      items_being_loaded: false,
      global_vars,
      date_range: [],
      picked_date_range: null,
      sort_asc: false,

      search_invoice: '',
      search_sku: '',
      picked_user: null,
      picked_location: null,

      receiveLocsub: {},
      receiveQty: {},
      busy: {},

      bulkBusy: false,
    };
  },
  computed: {
    salespersonOptions() {
      const seen = new Map();
      this.items.forEach(r => {
        const u = r.salesperson || r.username;
        if (u && !seen.has(u)) seen.set(u, u);
      });
      const opts = [{ value: null, text: 'All salespeople' }];
      Array.from(seen.keys()).sort().forEach(u => opts.push({ value: u, text: u }));
      return opts;
    },

    locationOptions() {
      const opts = [{ value: null, text: 'All locations' }];
      (this.packet.locations || []).forEach(l => {
        if (l && l.value != null) opts.push({ value: l.value, text: l.text });
      });
      return opts;
    },

    filter() {
      const that = this;
      const inv  = (this.search_invoice || '').trim().toLowerCase();
      const sku  = (this.search_sku     || '').trim().toLowerCase();

      return this.items.filter(function (d) {
        if (inv && !(d.invoice_id || '').toLowerCase().includes(inv)) return false;
        if (sku && !(d.product_sku || '').toLowerCase().includes(sku)) return false;

        if (that.picked_user) {
          const u = d.salesperson || d.username;
          if (String(u) !== String(that.picked_user)) return false;
        }

        if (that.picked_location && String(d.location_id) !== String(that.picked_location)) return false;

        return true;
      });
    },

    totalPendingUnits() {
      return this.filter.reduce((s, r) => s + (parseInt(r.pending_quantity) || 0), 0);
    },
    totalReceivedUnits() {
      return this.filter.reduce((s, r) => s + (parseInt(r.received_quantity) || 0), 0);
    },
    dateLabel() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} → ${this.date_range[1]}`;
      }
      return 'All dates';
    },
  },
  methods: {
    /* ---------- Date formatting (date only, no time) ---------- */
    formatDateOnly(value) {
      if (!value) return '—';
      const m = moment(value);
      return m.isValid() ? m.format('MM/DD/YYYY') : String(value);
    },

    /* ---------- Customer helpers ---------- */
    customerName(item) {
      const full = this.customerFullName(item);
      if (!full) return '—';
      return full.length > 22 ? full.slice(0, 20) + '…' : full;
    },
    customerFullName(item) {
      if (!item) return '';
      const raw = item.customer;
      if (raw == null) return '';

      if (typeof raw === 'object') {
        const first = raw.first_name || raw.firstname || '';
        const last  = raw.last_name  || raw.lastname  || '';
        const full  = (first + ' ' + last).trim();
        return full || raw.fullname || raw.name || '';
      }

      const s = String(raw).trim();

      if (s.startsWith('{') && s.endsWith('}')) {
        try {
          const obj = JSON.parse(s);
          const first = obj.first_name || obj.firstname || '';
          const last  = obj.last_name  || obj.lastname  || '';
          const full  = (first + ' ' + last).trim();
          return full || obj.fullname || obj.name || '';
        } catch (e) { /* fall through */ }
      }

      const parts = s.split(/\s*[|•·]\s*/);
      return (parts[0] || '').trim();
    },

    /* ---------- Location helpers ---------- */
    locName(locsub) {
      if (!locsub) return '';
      const key = String(locsub);
      const found = (this.packet.locsubs || []).find(l => String(l.value) === key);
      if (found) {
        return found.text || found.name || found.label || key;
      }
      return key;
    },

    // getReturnLocationOptions(row) {
    //   const opts = [];
    //   const seen = new Set();
    //   const push = (locsub) => {
    //     if (!locsub) return;
    //     const key = String(locsub);
    //     if (seen.has(key)) return;
    //     const found = (this.packet.locsubs || []).find(l => String(l.value) === key);
    //     if (!found) return;
    //     seen.add(key);
    //     opts.push({ value: key, text: found.text || found.name || found.label || key });
    //   };

    //   push(row.original_locsub);
    //   push(row.drop_locsub);
    //   push(row.ship_locsub);
    //   push(MECCA_RETURN_LOCSUB);

    //   const storeLocId = row.location_id;
    //   (this.packet.locsubs || []).forEach(loc => {
    //     if (!loc.value) return;
    //     const parts = String(loc.value).split('|');
    //     if (parseInt(parts[0]) !== parseInt(storeLocId)) return;
    //     const t = ((loc.text || loc.name || '') + '').toUpperCase();
    //     if (t.includes('STOCK') || t.includes('FLOOR')) push(loc.value);
    //   });

    //   return opts;
    // },
    
    getReturnLocationOptions(row) {
  const opts = [];
  const seen = new Set();
  const push = (locsub) => {
    if (!locsub) return;
    const key = String(locsub);
    if (seen.has(key)) return;
    const found = (this.packet.locsubs || []).find(l => String(l.value) === key);
    if (!found) return;
    seen.add(key);
    opts.push({ value: key, text: found.text || found.name || found.label || key });
  };

  // 1. Mecca Shipping — RETURN TO INVENTORY (always first)
  push(MECCA_RETURN_LOCSUB);

  // 2. Store's own STOCK / FLOOR sublocations only
  const storeLocId = row.location_id;
  (this.packet.locsubs || []).forEach(loc => {
    if (!loc.value) return;
    const parts = String(loc.value).split('|');
    if (parseInt(parts[0]) !== parseInt(storeLocId)) return;
    const t = ((loc.text || loc.name || '') + '').toUpperCase();
    if (t.includes('STOCK') || t.includes('FLOOR')) push(loc.value);
  });

  return opts;
},
    /* ---------- Receive (single row) ---------- */
    markReceived(item) {
      const rid = item.receipt_id;
      const qty = parseInt(this.receiveQty[rid]);
      const maxQty = parseInt(item.pending_quantity) || 0;
      const locsub = this.receiveLocsub[rid];

      if (!qty || qty <= 0) {
        this.$bvToast.toast('Enter the quantity received.', { title: 'Check', variant: 'warning', autoHideDelay: 2000 });
        return Promise.reject(new Error('invalid qty'));
      }
      if (qty > maxQty) {
        this.$bvToast.toast(`Cannot receive more than ${maxQty} pending unit(s).`, { title: 'Check', variant: 'warning', autoHideDelay: 2500 });
        return Promise.reject(new Error('qty over max'));
      }
      if (!locsub) {
        this.$bvToast.toast('Pick the return-to location.', { title: 'Check', variant: 'warning', autoHideDelay: 2000 });
        return Promise.reject(new Error('no locsub'));
      }

      this.$set(this.busy, rid, true);
      const that = this;

      return new Promise((resolve, reject) => {
        Requestor.postAsyncP(
          "/api/sales/mark_item_received/",
          {
            receipt_id: rid,
            received_quantity: qty,
            received_by: this.packet.userDetails.username,
            return_locsub: locsub,
          },
          this,
          function (response) {
            that.$set(that.busy, rid, false);
            if (response.data.error) {
              that.$bvToast.toast(response.data.error, { title: "Error", variant: "warning", autoHideDelay: 3000 });
              reject(new Error(response.data.error));
              return;
            }

            const updated = response.data.result || {};
            that.$bvToast.toast(
              updated.state === 'received'
                ? `${item.product_sku} fully received.`
                : `${item.product_sku} partially received (${updated.received_quantity}/${updated.return_quantity}).`,
              { title: "Received", variant: "success", autoHideDelay: 2000 }
            );

            if (updated.state === 'received') {
              that.items = that.items.filter(r => r.receipt_id !== rid);
            } else {
              const idx = that.items.findIndex(r => r.receipt_id === rid);
              if (idx !== -1) that.$set(that.items, idx, { ...that.items[idx], ...updated });
              that.$set(that.receiveLocsub, rid, updated.return_locsub || locsub);
              that.$set(that.receiveQty, rid, updated.pending_quantity || 0);
            }
            resolve(updated);
          },
          function () {
            that.$set(that.busy, rid, false);
            that.$bvToast.toast("Error marking item received.", { title: "Error", variant: "danger", autoHideDelay: 3000 });
            reject(new Error('network'));
          }
        );
      });
    },

    /* ---------- Receive (bulk) ---------- */
    async markAllReceived() {
      const rows = this.filter.slice();
      if (!rows.length) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Receive ${rows.length} line(s) using each row's chosen return-to location?`,
        {
          title: 'Receive all merchandise',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Receive all',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true,
        }
      );
      if (!confirmed) return;

      rows.forEach(r => {
        if (!this.receiveLocsub[r.receipt_id]) {
          const opts = this.getReturnLocationOptions(r);
          this.$set(this.receiveLocsub, r.receipt_id, opts.length ? opts[0].value : MECCA_RETURN_LOCSUB);
        }
        if (!this.receiveQty[r.receipt_id] || this.receiveQty[r.receipt_id] <= 0) {
          this.$set(this.receiveQty, r.receipt_id, parseInt(r.pending_quantity) || 0);
        }
      });

      this.bulkBusy = true;
      let ok = 0, fail = 0;

      for (const row of rows) {
        try {
          await this.markReceived(row);
          ok++;
        } catch (e) {
          fail++;
        }
      }

      this.bulkBusy = false;

      this.$bvToast.toast(
        fail === 0
          ? `Received ${ok} line(s).`
          : `Received ${ok} line(s), ${fail} failed. Check the log.`,
        {
          title: 'Bulk receive',
          variant: fail === 0 ? 'success' : 'warning',
          autoHideDelay: 3000,
        }
      );
    },

    /* ---------- Navigation ---------- */
    goToSale(invoiceId) {
      this.$router.push("/sales/viewsale/" + invoiceId + "/");
    },

    sortby(key) {
      if (this.sort_asc) this.items = Requestor.sort(this.items, key);
      else this.items = Requestor.sort_reverse(this.items, key);
      this.sort_asc = !this.sort_asc;
    },

    /* ---------- Dates ---------- */
    set_dates(value) {
      let start = null, end = null;
      if (value === 'today')      { start = moment().format("MM-DD-YYYY"); end = start; }
      if (value === 'yesterday')  { start = moment().add(-1, 'days').format("MM-DD-YYYY"); end = start; }
      if (value === 'this_week')  { start = moment().startOf('week').format("MM-DD-YYYY"); end = moment().endOf('week').format("MM-DD-YYYY"); }
      if (value === 'last_week')  { start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY"); end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY"); }
      if (value === 'this_month') { start = moment().startOf('month').format("MM-DD-YYYY"); end = moment().endOf('month').format("MM-DD-YYYY"); }
      if (value === 'last_month') { start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY"); end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY"); }

      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },

    resetFilters() {
      this.search_invoice = '';
      this.search_sku = '';
      this.picked_user = null;
      this.picked_location = null;
      this.date_range = [];
      this.picked_date_range = null;
      this.fetch_init();
    },

    /* ---------- Fetch ---------- */
    fetch_init() {
      const dates = this.date_range;
      const that = this;

      const data = {
        username: this.packet.userDetails.username,
        location_id: (this.packet.userDetails.properties.location_id || null),
        search_txt: this.search_sku || '',
      };
      if (dates && dates.length >= 2) {
        data['dates[0]'] = dates[0];
        data['dates[1]'] = dates[1];
      }

      const s = function (response) {
        if (response.data.result) {
          that.items = response.data.result;

          const seedLoc = {};
          const seedQty = {};
          that.items.forEach(r => {
            seedQty[r.receipt_id] = r.pending_quantity > 0 ? r.pending_quantity : 0;
            if (r.return_locsub) {
              seedLoc[r.receipt_id] = r.return_locsub;
            } else {
              const opts = that.getReturnLocationOptions(r);
              seedLoc[r.receipt_id] = opts.length ? opts[0].value : MECCA_RETURN_LOCSUB;
            }
          });
          that.receiveLocsub = seedLoc;
          that.receiveQty = seedQty;
          that.busy = {};
        } else {
          that.$bvToast.toast(response.data.error, { title: "Error", variant: "warning", autoHideDelay: 2000 });
        }
        that.items_being_loaded = false;
      };

      const f = function () {
        that.$bvToast.toast("Error occurred. Please try again.", { title: "Error", variant: "danger", autoHideDelay: 2000 });
        that.items_being_loaded = false;
      };

      this.items_being_loaded = true;
      this.items = [];

      Requestor.getAsyncP("/api/sales/list_merchandise_due/", data, this, s, f);
    },
  },

  created() {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
  },
};
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.md-page {
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 17px;
}
.md-wrap { max-width: 1800px; width: 97%; margin: 0 auto; }
@media (max-width: 767px) {
  .md-page { padding: 10px 0 40px; font-size: 16px; }
  .md-wrap { width: 100%; padding: 0 12px; }
}

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
.md-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
}
.md-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.md-hero-label {
  font-size: 15px; color: rgba(255, 255, 255, 0.6);
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
}
.md-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 42px; font-weight: 800;
  line-height: 1.1;
  margin: 4px 0 12px;
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
}
.md-count-pill {
  display: inline-flex; align-items: center; justify-content: center;
  background: #EE7A22; color: #fff;
  font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800;
  padding: 5px 16px; border-radius: 999px;
}
.md-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.md-tag {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 15px; font-weight: 700;
  padding: 6px 15px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.md-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.md-tag.is-teal   { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.md-hero-sub {
  color: rgba(255, 255, 255, 0.62);
  font-size: 15px; line-height: 1.55;
  margin: 0; max-width: 70ch;
}
.md-hero-qr {
  display: flex; align-items: center; gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px; padding: 14px 20px;
  color: #FFB06B;
}
.md-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 17px; color: #fff; }
.md-qr-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }
.md-hero-actions {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 12px 30px; background: #34302B;
}
.md-hero-actions .btn { padding: 9px 18px !important; font-size: 16px !important; }
@media (max-width: 900px) {
  .md-hero-grid { grid-template-columns: 1fr; gap: 14px; }
  .md-hero-title { font-size: 32px; }
  .md-hero-actions { padding: 10px 18px; }
}

/* ===================================================== */
/* BUTTONS                                               */
/* ===================================================== */
.md-btn-primary.btn,
.md-btn-ghost.btn,
.md-btn-ghost-dark.btn {
  border-radius: 10px !important;
  font-weight: 700; font-size: 16.5px;
  padding: 10px 18px;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow: none !important;
}
.md-btn-primary.btn {
  background: #EE7A22 !important;
  border: 1.5px solid #EE7A22 !important;
  color: #fff !important;
}
.md-btn-primary.btn:hover:not(:disabled) {
  background: #D4620F !important;
  border-color: #D4620F !important;
}
.md-btn-ghost.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
}
.md-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.md-btn-ghost-dark.btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fff !important;
  border: 1.5px solid rgba(255, 255, 255, 0.22) !important;
}
.md-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.md-page .btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* CARDS                                                 */
/* ===================================================== */
.md-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
}

/* ===================================================== */
/* SINGLE-ROW FILTER STRIP                               */
/* ===================================================== */
.md-filterstrip { padding: 12px 14px !important; }
.md-filters-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 3px;
  scrollbar-width: thin;
}
.md-filters-bar::-webkit-scrollbar { height: 8px; }
.md-filters-bar::-webkit-scrollbar-thumb { background: #DCD6CC; border-radius: 8px; }

.md-fb-item { flex: 0 0 auto; min-width: 0; }
.md-fb-search { flex: 1 1 240px; min-width: 200px; }
.md-fb-dates  { flex: 0 1 260px; min-width: 220px; }

.md-fb-datepicker { width: 100%; display: block; }
.md-fb-datepicker >>> .mx-input {
  height: 48px;
  font-size: 17px;
  font-weight: 600;
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  background: #F7F5F2;
  padding: 0 14px;
  box-shadow: none;
}
.md-fb-datepicker >>> .mx-input:focus {
  border-color: #EE7A22;
  background: #fff;
}

.md-fb-inputgroup {
  flex-wrap: nowrap;
  width: 100%;
}
.md-fb-inputgroup >>> .input-group-text {
  height: 48px;
  padding: 0 14px;
  background: #F1EEE9;
  border: 1.5px solid #E5E1DA;
  border-right: 0;
  border-radius: 10px 0 0 10px !important;
  color: #736C63;
  font-size: 17px;
}
.md-fb-inputgroup >>> .form-control {
  height: 48px !important;
  font-size: 17px !important;
  font-weight: 600 !important;
  border: 1.5px solid #E5E1DA !important;
  background: #F7F5F2 !important;
  color: #221F1C !important;
  border-radius: 0 10px 10px 0 !important;
  padding: 0 14px !important;
}
.md-fb-inputgroup >>> .form-control:focus {
  border-color: #EE7A22 !important;
  background: #fff !important;
  box-shadow: none !important;
}

.md-fb-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 999px;
  padding: 0 16px 0 14px;
  height: 48px;
  flex-shrink: 0;
}
.md-fb-pill-icon { color: #EE7A22; font-size: 17px; flex-shrink: 0; }
.md-fb-select {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 16.5px !important;
  font-weight: 700 !important;
  color: #221F1C !important;
  padding: 0 26px 0 4px !important;
  height: 46px !important;
  min-width: 160px;
  background-position: right 6px center !important;
  background-size: 14px !important;
  cursor: pointer;
}
.md-fb-select:focus { box-shadow: none !important; outline: none !important; }

.md-fb-search-btn.btn { height: 48px; padding: 0 20px !important; flex-shrink: 0; }
.md-fb-reset.btn {
  height: 48px;
  width: 48px;
  padding: 0 !important;
  border-radius: 999px !important;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 17px;
}

/* Quick dates row */
.md-quick-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #E5E1DA;
}
.md-quick-btn.btn {
  background: #fff !important;
  color: #4A4540 !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 999px !important;
  font-size: 13.5px !important;
  font-weight: 700 !important;
  padding: 5px 14px !important;
  box-shadow: none !important;
}
.md-quick-btn.btn:hover { border-color: #EE7A22 !important; color: #D4620F !important; }
.md-quick-btn.btn.is-active {
  background: #EE7A22 !important;
  border-color: #EE7A22 !important;
  color: #fff !important;
}

@media (max-width: 900px) {
  .md-filters-bar { flex-wrap: wrap; overflow-x: visible; }
  .md-fb-search, .md-fb-dates { flex: 1 1 100%; min-width: 0; }
  .md-fb-pill { flex: 1 1 calc(50% - 5px); }
  .md-fb-select { min-width: 0; width: 100%; }
}

/* ===================================================== */
/* TABLE                                                 */
/* ===================================================== */
.md-table-card { padding: 0 !important; overflow: hidden; }
.md-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.md-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 17px;
  min-width: 1740px;
}
.md-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #221F1C;
  color: #fff;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 18px 14px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  border-bottom: 3px solid #EE7A22;
}
.md-th:hover { background: #34302B; }
.md-th--num { text-align: center; }
.md-th--actions { cursor: default; text-align: center; }
.md-th--actions:hover { background: #221F1C; }
.md-sort-icon { font-size: 13px; opacity: 0.6; margin-left: 5px; }

/* Narrow, compact date column */
.md-th--date {
  min-width: 96px;
  width: 96px;
  text-align: center;
}
.md-td--date {
  text-align: center;
  padding: 14px 8px;
}

.md-row {
  animation: mdRowIn 0.35s ease-out backwards;
  transition: background 0.15s ease;
}
@keyframes mdRowIn {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}
.md-td {
  padding: 14px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: middle;
  background: #fff;
  color: #221F1C;
}
.md-row:hover .md-td { background: #FAF9F7; }
.md-td--num { text-align: center; }
.md-td--actions { text-align: center; }

.md-date-cell {
  font-family: 'Menlo', monospace;
  font-size: 13.5px;
  font-weight: 700;
  color: #4A4540;
  white-space: nowrap;
  letter-spacing: -0.01em;
}

.md-invoice-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #D4620F;
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 8px;
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  transition: background 0.15s ease;
  white-space: nowrap;
}
.md-invoice-link:hover {
  background: #FFE2C7;
  color: #B5520B;
}

.md-cust-cell { display: flex; flex-direction: column; gap: 2px; }
.md-cust-name {
  font-weight: 800;
  font-size: 15.5px;
  color: #221F1C;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 230px;
}
.md-item-desc {
  font-size: 15.5px;
  font-weight: 600;
  color: #34302B;
  line-height: 1.35;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.md-sku {
  font-family: 'Menlo', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #4A4540;
  background: #F1EEE9;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-block;
  white-space: nowrap;
}

.md-chip {
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 800;
  padding: 5px 12px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  min-width: 42px;
}
.md-chip.is-teal { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.md-chip.is-warn { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }

.md-locsub-select >>> .custom-select {
  height: 42px;
  border: 1.5px solid #E5E1DA;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 600;
  color: #221F1C;
  background: #F7F5F2;
  padding: 0 32px 0 12px;
}
.md-locsub-select >>> .custom-select:focus {
  border-color: #EE7A22;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}
.md-locsub-select >>> .custom-select:disabled {
  background: #F1EEE9;
  color: #A59D92;
}

.md-qty-input >>> .form-control {
  height: 42px;
  border: 1.5px solid #E5E1DA;
  border-radius: 9px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  background: #F7F5F2;
  color: #221F1C;
}
.md-qty-input >>> .form-control:focus {
  border-color: #EE7A22;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18);
}

.md-btn-receive.btn {
  background: #0F6E6B !important;
  border: 1.5px solid #0F6E6B !important;
  color: #fff !important;
  border-radius: 9px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  padding: 8px 16px !important;
  display: inline-flex; align-items: center; gap: 6px;
  box-shadow: none !important;
  white-space: nowrap;
}
.md-btn-receive.btn:hover:not(:disabled) {
  background: #0B5552 !important;
  border-color: #0B5552 !important;
}
.md-btn-receive.btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* EMPTY                                                 */
/* ===================================================== */
.md-empty {
  text-align: center;
  padding: 60px 30px;
  border: 2px dashed #D6CFC4;
  background: #FAF9F7;
  border-radius: 16px;
}
.md-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #E2F2F0; color: #0F6E6B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.md-empty h3 { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; margin: 0 0 8px; color: #221F1C; }
.md-empty p { color: #736C63; max-width: 50ch; margin: 0 auto; line-height: 1.6; font-size: 16px; }

/* ===================================================== */
/* POP ANIMATION                                         */
/* ===================================================== */
.md-pop-enter-active { animation: mdPopIn 0.28s cubic-bezier(0.34, 1.2, 0.64, 1); }
@keyframes mdPopIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
  .md-row, .md-count-pill { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/MerchandiseDue.vue